import { useState } from 'react';
import { supabase } from '@/lib/supabase';

interface ReviewModalProps {
  order: any;
  vendedorId: string;
  productoId: string;
  compradorId: string;
  onClose: () => void;
  onSubmitted: () => void;
}

const ReviewModal = ({ order, vendedorId, productoId, compradorId, onClose, onSubmitted }: ReviewModalProps) => {
  const [estrellas, setEstrellas] = useState(0);
  const [hoverEstrellas, setHoverEstrellas] = useState(0);
  const [comentario, setComentario] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (estrellas === 0) { setError('Por favor selecciona una calificación'); return; }
    setSubmitting(true);
    setError('');

    try {
      // Insertar la reseña
      const { error: insertError } = await supabase
        .from('reviews')
        .insert({
          order_id: order.id,
          comprador_id: compradorId,
          vendedor_id: vendedorId,
          producto_id: productoId,
          estrellas,
          comentario: comentario.trim() || null,
        });

      if (insertError) throw insertError;

      // Actualizar reputación del vendedor (promedio de estrellas)
      const { data: allReviews } = await supabase
        .from('reviews')
        .select('estrellas')
        .eq('vendedor_id', vendedorId);

      if (allReviews && allReviews.length > 0) {
        const promedio = allReviews.reduce((sum, r) => sum + r.estrellas, 0) / allReviews.length;
        await supabase
          .from('profiles')
          .update({ reputacion: Math.round(promedio * 10) / 10 })
          .eq('id', vendedorId);
      }

      onSubmitted();
    } catch (err: any) {
      setError(err.message || 'Error al enviar la reseña');
    } finally {
      setSubmitting(false);
    }
  };

  const etiquetasEstrellas = ['', 'Muy malo', 'Malo', 'Regular', 'Bueno', '¡Excelente!'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">Califica al Vendedor</h3>
            <p className="text-sm text-gray-500 line-clamp-1">{order.products?.titulo}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Estrellas */}
        <div className="text-center mb-6">
          <p className="text-sm font-semibold text-gray-700 mb-3">¿Cómo fue tu experiencia?</p>
          <div className="flex items-center justify-center gap-2 mb-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <button
                key={s}
                onClick={() => setEstrellas(s)}
                onMouseEnter={() => setHoverEstrellas(s)}
                onMouseLeave={() => setHoverEstrellas(0)}
                className="cursor-pointer transition-transform hover:scale-110"
              >
                <i className={`text-4xl ${
                  s <= (hoverEstrellas || estrellas)
                    ? 'ri-star-fill text-amber-400'
                    : 'ri-star-line text-gray-300'
                }`}></i>
              </button>
            ))}
          </div>
          {(hoverEstrellas || estrellas) > 0 && (
            <p className="text-sm font-semibold text-amber-600 animate-pulse">
              {etiquetasEstrellas[hoverEstrellas || estrellas]}
            </p>
          )}
        </div>

        {/* Comentario */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Comentario <span className="text-gray-400 font-normal">(opcional)</span>
          </label>
          <textarea
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Cuéntanos sobre tu experiencia con este vendedor..."
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-400 text-sm resize-none"
          />
          <p className="text-xs text-gray-400 text-right mt-1">{comentario.length}/500</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-center gap-2">
            <i className="ri-error-warning-line text-red-500"></i>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Botones */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || estrellas === 0}
            className="flex-1 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors whitespace-nowrap cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Enviando...
              </span>
            ) : (
              <>
                <i className="ri-star-line mr-2"></i>
                Enviar Reseña
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
